"use client";

import { useMemo, useState } from "react";
import {
  Notice,
  Panel,
  PillGroup,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  HINT,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import {
  GrantBudgetResults,
  type GrantResultLabels,
} from "@/components/tools/research-ops/grant-budget-results";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseIntegerInput, parseNumberInput } from "@/lib/parse-number";
import {
  type BaseKind,
  computeBudget,
  DEFAULT_EQUIPMENT_THRESHOLD,
  DEFAULT_SUBAWARD_THRESHOLD,
  MAX_YEARS,
  parseAmounts,
  parseEquipment,
  parsePersonnel,
} from "@/lib/tool-kit/research-ops/grant-budget";

/** English strings. A translated content file overrides these through `ui`. */
export const GRANT_BUDGET_CALCULATOR_STRINGS = {
  tryExample: "Try an example",
  clear: "Clear the form",
  awardPanel: "The award",
  yearsLabel: "Project length (years)",
  yearsHint: "1 to 10.",
  salaryEscLabel: "Annual salary increase (%)",
  salaryEscHint:
    "Applied from year 2 and compounded. Use your institution's policy.",
  otherEscLabel: "Annual increase for other costs (%)",
  otherEscHint:
    "Applied to travel, supplies, participant support and the costs outside the base. Many funders allow none; leave 0 unless your call lets you inflate.",
  fringeLabel: "Fringe benefit rate (%)",
  fringeHint:
    "The default for every person; a fourth field on a line overrides it.",
  baseLegend: "Indirect costs are charged on",
  baseMtdc: "Modified total direct costs (MTDC)",
  baseTdc: "Total direct costs (TDC)",
  baseMtdcNote:
    "Under 2 CFR 200.1 the MTDC base leaves out equipment, participant support, tuition remission, scholarships and fellowships, rental costs, patient care, capital expenditures, and the part of each subaward above the threshold.",
  baseTdcNote:
    "Some negotiated agreements use total direct costs: every direct cost goes into the base.",
  rateLabel: "Indirect (F&A) rate (%)",
  rateHint:
    "Enter the rate in your institution's current agreement or your sponsor's rules. This tool does not know any institution's rate.",
  equipmentLabel: "Equipment threshold ($ per unit)",
  equipmentHint:
    "The lesser of your institution's capitalization level and $10,000 (2 CFR 200.1, from 2024). Items below it are counted as supplies.",
  subawardLabel: "Subaward amount inside the MTDC base ($ each)",
  subawardHint:
    "$50,000 for each subaward over the life of the award (2 CFR 200.1, from 2024).",
  currency: "$",
  linesPanel: "Budget lines",
  linesHint:
    "One item per line. Separate fields with commas, tabs, semicolons or pipes, and write amounts without thousands separators (62000, not 62,000). The last field is optional: the year (2) or years (1-3) the item applies to; leave it out for every year.",
  categories: {
    personnel:
      "Personnel — role, annual base salary, effort %, fringe % (optional)",
    equipment: "Equipment — item, cost, year (optional)",
    travel: "Travel — item, amount per year, years (optional)",
    supplies:
      "Materials, supplies and services — item, amount per year, years (optional)",
    subawards: "Subawards — name, amount per year, years (optional)",
    participants: "Participant support — item, amount, years (optional)",
    excluded:
      "Other costs outside the base (tuition remission, rent, patient care) — item, amount, years (optional)",
  },
  placeholders: {
    personnel: "Postdoctoral researcher, 62000, 100\nPI, 110000, 8, 30",
    equipment: "Eye tracker, 24000, 1",
    travel: "Conference travel, 3000",
    supplies: "Software and consumables, 2500",
    subawards: "Partner university, 40000, 1-3",
    participants: "Workshop stipends, 5000, 2",
    excluded: "Tuition remission, 9000",
  },
  linesRead: "one: Read {count} line.|other: Read {count} lines.",
  linesSkipped: "Could not read line {numbers}: {text}",
  resultsHeading: "Budget",
  prompt: "Add budget lines to see the multi-year budget.",
  statDirect: "Total direct costs",
  statIndirect: "Indirect costs",
  statTotal: "Total request",
  colItem: "Budget line",
  colYear: "Year {year}",
  colTotal: "Total",
  rowSalaries: "Salaries and wages",
  rowFringe: "Fringe benefits",
  rowEquipment: "Equipment",
  rowTravel: "Travel",
  rowSupplies: "Materials, supplies and services",
  rowSubawards: "Subawards",
  rowParticipants: "Participant support",
  rowExcluded: "Other costs outside the base",
  rowDirect: "Total direct costs",
  rowBaseMtdc: "MTDC base",
  rowBaseTdc: "TDC base",
  rowIndirect: "Indirect costs at {rate}%",
  rowTotal: "Total request",
  copy: "Copy for a spreadsheet",
  copied: "Copied!",
  downloadCsv: "Download CSV",
  csvFile: "grant-budget.csv",
  noteReclassified:
    "Below the equipment threshold, so counted as supplies and included in the base: {items}.",
  noteSubaward:
    "{name}: {excess} of the award's subaward costs is above the threshold and sits outside the MTDC base.",
  noteNoRate:
    "No indirect rate entered, so indirect costs are zero. Enter your rate above.",
  rounding:
    "Amounts are rounded to whole dollars line by line, as most budget forms require, so the columns add up exactly.",
  example: {
    years: "3",
    salaryEsc: "3",
    otherEsc: "0",
    fringe: "28",
    rate: "15",
    personnel:
      "Principal investigator, 110000, 8\nPostdoctoral researcher, 62000, 100\nGraduate research assistant, 30000, 50, 8",
    equipment: "Eye tracker, 24000, 1\nLaptop, 1800, 1",
    travel: "Conference travel, 3000",
    supplies: "Software and consumables, 2500",
    subawards: "Partner university, 40000, 1-3",
    participants: "Workshop stipends, 5000, 2",
    excluded: "Tuition remission, 9000",
  },
} as const;

type TextKey =
  | "personnel"
  | "equipment"
  | "travel"
  | "supplies"
  | "subawards"
  | "participants"
  | "excluded";

const TEXT_KEYS: readonly TextKey[] = [
  "personnel",
  "equipment",
  "travel",
  "supplies",
  "subawards",
  "participants",
  "excluded",
];

const BLANK_TEXT: Record<TextKey, string> = {
  personnel: "",
  equipment: "",
  travel: "",
  supplies: "",
  subawards: "",
  participants: "",
  excluded: "",
};

function numberOr(text: string, fallback: number): number {
  return parseNumberInput(text) ?? fallback;
}

interface ParsedLines {
  personnel: ReturnType<typeof parsePersonnel>;
  equipment: ReturnType<typeof parseEquipment>;
  travel: ReturnType<typeof parseAmounts>;
  supplies: ReturnType<typeof parseAmounts>;
  subawards: ReturnType<typeof parseAmounts>;
  participants: ReturnType<typeof parseAmounts>;
  excluded: ReturnType<typeof parseAmounts>;
}

function readLines(texts: Record<TextKey, string>, years: number): ParsedLines {
  return {
    personnel: parsePersonnel(texts.personnel),
    equipment: parseEquipment(texts.equipment, years),
    travel: parseAmounts(texts.travel, years),
    supplies: parseAmounts(texts.supplies, years),
    subawards: parseAmounts(texts.subawards, years),
    participants: parseAmounts(texts.participants, years),
    excluded: parseAmounts(texts.excluded, years),
  };
}

export default function GrantBudgetCalculatorWidget() {
  const strings = useToolUi(GRANT_BUDGET_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [yearsText, setYearsText] = useState("3");
  const [salaryEsc, setSalaryEsc] = useState("3");
  const [otherEsc, setOtherEsc] = useState("0");
  const [fringe, setFringe] = useState("");
  const [rate, setRate] = useState("");
  const [base, setBase] = useState<BaseKind>("mtdc");
  const [equipmentThreshold, setEquipmentThreshold] = useState(
    String(DEFAULT_EQUIPMENT_THRESHOLD)
  );
  const [subawardThreshold, setSubawardThreshold] = useState(
    String(DEFAULT_SUBAWARD_THRESHOLD)
  );
  const [texts, setTexts] = useState<Record<TextKey, string>>(BLANK_TEXT);

  const years = Math.min(
    MAX_YEARS,
    Math.max(1, parseIntegerInput(yearsText) ?? 1)
  );
  const indirectRate = Math.max(0, numberOr(rate, 0));
  const read = useMemo(() => readLines(texts, years), [texts, years]);
  const result = useMemo(
    () =>
      computeBudget({
        years,
        salaryEscalation: numberOr(salaryEsc, 0),
        otherEscalation: numberOr(otherEsc, 0),
        fringe: Math.max(0, numberOr(fringe, 0)),
        equipmentThreshold: numberOr(
          equipmentThreshold,
          DEFAULT_EQUIPMENT_THRESHOLD
        ),
        subawardThreshold: numberOr(
          subawardThreshold,
          DEFAULT_SUBAWARD_THRESHOLD
        ),
        base,
        indirectRate,
        personnel: read.personnel.lines,
        equipment: read.equipment.lines,
        travel: read.travel.lines,
        supplies: read.supplies.lines,
        subawards: read.subawards.lines,
        participants: read.participants.lines,
        excluded: read.excluded.lines,
      }),
    [
      years,
      salaryEsc,
      otherEsc,
      fringe,
      equipmentThreshold,
      subawardThreshold,
      base,
      indirectRate,
      read,
    ]
  );
  const hasLines = TEXT_KEYS.some((key) => read[key].lines.length > 0);

  const loadExample = () => {
    const sample = strings.example;
    setYearsText(sample.years);
    setSalaryEsc(sample.salaryEsc);
    setOtherEsc(sample.otherEsc);
    setFringe(sample.fringe);
    setRate(sample.rate);
    setBase("mtdc");
    setTexts({
      personnel: sample.personnel,
      equipment: sample.equipment,
      travel: sample.travel,
      supplies: sample.supplies,
      subawards: sample.subawards,
      participants: sample.participants,
      excluded: sample.excluded,
    });
  };
  const clear = () => {
    setTexts(BLANK_TEXT);
    setFringe("");
    setRate("");
  };
  const labels: GrantResultLabels = strings;

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={loadExample}
          type="button"
        >
          {strings.tryExample}
        </button>
        <button className={BUTTON_SECONDARY} onClick={clear} type="button">
          {strings.clear}
        </button>
      </div>

      <Panel title={strings.awardPanel}>
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            hint={strings.yearsHint}
            id="gb-years"
            label={strings.yearsLabel}
            numeric
            onChange={setYearsText}
            value={yearsText}
          />
          <TextField
            hint={strings.fringeHint}
            id="gb-fringe"
            label={strings.fringeLabel}
            numeric
            onChange={setFringe}
            value={fringe}
          />
          <TextField
            hint={strings.salaryEscHint}
            id="gb-salary-esc"
            label={strings.salaryEscLabel}
            numeric
            onChange={setSalaryEsc}
            value={salaryEsc}
          />
          <TextField
            hint={strings.otherEscHint}
            id="gb-other-esc"
            label={strings.otherEscLabel}
            numeric
            onChange={setOtherEsc}
            value={otherEsc}
          />
        </div>
        <PillGroup
          label={strings.baseLegend}
          name="gb-base"
          onChange={setBase}
          options={[
            { value: "mtdc", label: strings.baseMtdc },
            { value: "tdc", label: strings.baseTdc },
          ]}
          value={base}
        />
        <p className={HINT}>
          {base === "mtdc" ? strings.baseMtdcNote : strings.baseTdcNote}
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            hint={strings.rateHint}
            id="gb-rate"
            label={strings.rateLabel}
            numeric
            onChange={setRate}
            value={rate}
          />
          <TextField
            hint={strings.equipmentHint}
            id="gb-equipment-threshold"
            label={strings.equipmentLabel}
            numeric
            onChange={setEquipmentThreshold}
            value={equipmentThreshold}
          />
          {base === "mtdc" ? (
            <TextField
              hint={strings.subawardHint}
              id="gb-subaward-threshold"
              label={strings.subawardLabel}
              numeric
              onChange={setSubawardThreshold}
              value={subawardThreshold}
            />
          ) : null}
        </div>
      </Panel>

      <Panel title={strings.linesPanel}>
        <p className={HINT}>{strings.linesHint}</p>
        {TEXT_KEYS.map((key) => (
          <div className="space-y-1" key={key}>
            <TextAreaField
              id={`gb-${key}`}
              label={strings.categories[key]}
              onChange={(value) =>
                setTexts((previous) => ({ ...previous, [key]: value }))
              }
              placeholder={strings.placeholders[key]}
              rows={3}
              value={texts[key]}
            />
            {texts[key].trim() === "" ? null : (
              <p className={HINT}>
                {tPlural(strings.linesRead, locale, read[key].lines.length, {
                  count: fmt(read[key].lines.length),
                })}{" "}
                {read[key].skipped.map((item) =>
                  t(strings.linesSkipped, {
                    numbers: fmt(item.line),
                    text: item.text,
                  })
                )}
              </p>
            )}
          </div>
        ))}
      </Panel>

      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
        {hasLines ? (
          <GrantBudgetResults
            baseIsMtdc={base === "mtdc"}
            labels={labels}
            rate={indirectRate}
            result={result}
          />
        ) : (
          <Notice>{strings.prompt}</Notice>
        )}
        <p className={HINT}>{strings.rounding}</p>
      </section>
    </div>
  );
}
